import { useState, useEffect } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import { fetchData, deleteData } from '../../../apiService';
import { useAuth } from '../../../AuthContext';
import { tokenIsExpired } from '../../../authUtils';
import Button from '@mui/material/Button';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import { Fade, Tooltip, TextField, InputAdornment } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import DeliveryInModal from './DeliveryInModal';
import RentInvoiceModal from './RentInvoiceModal';
import Pagination from '../../common/Pagination';

function DeliveryInIndex() {
  const [data, setData] = useState([]);
  const [deleteItemId, setDeleteItemId] = useState(null);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [loading, setLoading] = useState(false);
  const { isAuthenticated, companyId, branchId, logout, token } = useAuth();

  // Modal states
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('view');
  const [selectedItem, setSelectedItem] = useState(null);

  // Invoice modal states
  const [invoiceModalOpen, setInvoiceModalOpen] = useState(false);
  const [selectedInvoiceItem, setSelectedInvoiceItem] = useState(null);

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Search state
  const [searchTerm, setSearchTerm] = useState('');
  const [searchExpanded, setSearchExpanded] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  // Date filter states
  const [fromDate, setFromDate] = useState(() => {
    const now = new Date();
    const firstDay = new Date(now.getFullYear(), now.getMonth(), 1);
    return firstDay.toISOString().split('T')[0];
  });
  const [toDate, setToDate] = useState(() => {
    const now = new Date();
    return now.toISOString().split('T')[0];
  });

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    setMounted(true);
    if (isAuthenticated && tokenIsExpired(token)) {
      logout();
      return;
    }
  }, [isAuthenticated, companyId]);

  useEffect(() => {
    if (loading || !companyId || !token) return;
    setLoading(true);
    fetchTableData().finally(() => {
      setLoading(false);
    });
  }, [companyId, token]);

  useEffect(() => {
    if (companyId && token && !loading) {
      fetchTableData();
    }
  }, [fromDate, toDate]);

  const fetchTableData = async () => {
    try {
      // Try DeliveryInList endpoint first
      let response = await fetchData('DeliveryInApi', `/DeliveryInList/${companyId}/${branchId}/${fromDate}/${toDate}`, token);
      console.log('DeliveryIn API Response:', response);

      // If DeliveryInList doesn't exist, fallback to DeliveryOutList and filter
      if (!response || response.responseCode !== '1111') {
        console.log('DeliveryInList not found, trying DeliveryInList with filter...');
        response = await fetchData('DeliveryInApi', `/DeliveryInList/${companyId}/${branchId}/${fromDate}/${toDate}`, token);

        if (response && response.responseCode === '1111' && response.response_data) {
          // Filter only completed delivery in records
          const filteredData = response.response_data.filter(item => item.IsDeliveryInCompleted === true);
          setData(filteredData);
          console.log('DeliveryIn data loaded (filtered):', filteredData);
          return;
        }
      }

      if (response && response.responseCode === '1111' && response.response_data) {
        setData(response.response_data);
        console.log('DeliveryIn data loaded:', response.response_data);
      } else {
        console.log('No data or invalid response');
        setData([]);
      }
    } catch (error) {
      console.error('Error fetching Delivery In Details:', error);
      setData([]);
    }
  };

  const handleDelete = async () => {
    try {
      const response = await deleteData('DeliveryInApi', deleteItemId, token);
      if (response && response.responseCode === '1111') {
        await fetchTableData();
      }
    } catch (error) {
      console.error('Error deleting Delivery In:', error);
    }
    setConfirmDialogOpen(false);
    setDeleteItemId(null);
  };

  const fetchSingleDelivery = async (deliveryId) => {
    try {
      const response = await fetchData('DeliveryInApi', `/DeliveryInFromDeliveryOut/${companyId}/${deliveryId}`, token);
      const data = response.response_data || response;
      const d = data.DeliveryCall || data.DeliveryInAndOut || data;
      const details = data.DeliveryCallDetails || data.DeliveryInAndOutDetails || [];
      return { ...d, Id: d.Id || deliveryId, DeliveryInAndOutDetails: details };
    } catch (error) {
      console.error('Error fetching single Delivery In:', error);
      return { Id: deliveryId };
    }
  };

  const openConfirmDialog = (itemId) => {
    setDeleteItemId(itemId);
    setConfirmDialogOpen(true);
  };

  const closeConfirmDialog = () => {
    setDeleteItemId(null);
    setConfirmDialogOpen(false);
  };

  const handleOpenModal = async (mode, item = null) => {
    if (item) {
      const fullData = await fetchSingleDelivery(item.Id);
      setSelectedItem(fullData);
    } else {
      setSelectedItem(null);
    }
    setModalMode(mode);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedItem(null);
  };

  const handleSaveDeliveryIn = () => {
    fetchTableData();
  };

  const handleOpenInvoiceModal = async (item) => {
    const fullData = await fetchSingleDelivery(item.Id);
    setSelectedInvoiceItem(fullData);
    setInvoiceModalOpen(true);
  };

  const handleCloseInvoiceModal = () => {
    setInvoiceModalOpen(false);
    setSelectedInvoiceItem(null);
  };

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
    setCurrentPage(1);
  };

  const filteredData = data.filter((item) => {
    const searchLower = searchTerm.toLowerCase();
    return (
      item.CustomerName?.toLowerCase().includes(searchLower) ||
      item.ContactNumber1?.toLowerCase().includes(searchLower) ||
      item.Location?.toLowerCase().includes(searchLower) ||
      item.DeliveryNo?.toLowerCase().includes(searchLower) ||
      item.DeliveryInVehicleNo?.toLowerCase().includes(searchLower)
    );
  });

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1);
  };

  const totalItems = filteredData.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedData = filteredData.slice(startIndex, endIndex);

  const columns = [
    { field: 'DeliveryNo', header: 'Delivery No' },
    { field: 'CustomerName', header: 'Customer' },
    { field: 'ContactNumber', header: 'Contact' },
    { field: 'Address', header: 'Address' },
    { field: 'DeliveryReturnDate', header: 'Delivery Return Date' },
    { field: 'IsInvoiced', header: 'Invoiced' },
  ];

  const renderActions = (item) => (
    <div style={{ display: 'flex', gap: '2px', alignItems: 'center', justifyContent: 'flex-end' }}>
      {!item.IsInvoiced && (
        <Tooltip title="Create Invoice" arrow>
          <Button
            size="small"
            onClick={() => handleOpenInvoiceModal(item)}
            style={{
              minWidth: '22px',
              height: '22px',
              borderRadius: '6px',
              color: 'white',
              padding: '2px',
              boxShadow: '0 1px 4px rgba(245, 158, 11, 0.3)',
              border: 'none',
              fontSize: '10px',
              fontWeight: '600'
            }}
          >
            🧾
          </Button>
        </Tooltip>
      )}

      <Tooltip title="View Details" arrow>
        <Button
          size="small"
          onClick={() => handleOpenModal('view', item)}
          style={{
            minWidth: '22px',
            height: '22px',
            borderRadius: '6px',
            color: 'white',
            padding: '2px',
            boxShadow: '0 1px 4px rgba(102, 126, 234, 0.3)',
            border: 'none',
            fontSize: '10px',
            fontWeight: '600'
          }}
        >
          👁️
        </Button>
      </Tooltip>

      {!item.IsInvoiced && (
        <Tooltip title="Edit Delivery In" arrow>
          <Button
            size="small"
            onClick={() => handleOpenModal('edit', item)}
            style={{
              minWidth: '22px',
              height: '22px',
              borderRadius: '6px',
              color: 'white',
              padding: '2px',
              boxShadow: '0 1px 4px rgba(17, 153, 142, 0.3)',
              border: 'none',
              fontSize: '10px',
              fontWeight: '600'
            }}
          >
            ✏️
          </Button>
        </Tooltip>
      )}

      {!item.IsInvoiced && (
        <Tooltip title="Delete Delivery In" arrow>
          <Button
            size="small"
            onClick={() => openConfirmDialog(item.Id)}
            style={{
              minWidth: '22px',
              height: '22px',
              borderRadius: '6px',
              color: 'white',
              padding: '2px',
              boxShadow: '0 1px 4px rgba(255, 107, 107, 0.3)',
              border: 'none',
              fontSize: '10px',
              fontWeight: '600'
            }}
          >
            🗑️
          </Button>
        </Tooltip>
      )}
      
    </div>
  );

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <Fade in={mounted} timeout={800}>
      <div style={{
        minWidth: '100%',
        background: '#f5f7fa',
        padding: '10px',
        position: 'relative'
      }}>
        <style>{`
          @keyframes slideIn {
            from { opacity: 0; transform: translateY(20px); }
            to { opacity: 1; transform: translateY(0); }
          }
          .action-button {
            text-transform: none;
            font-weight: 600;
            letter-spacing: 0.5px;
          }
          .table-row {
            /* No hover animations */
          }
        `}</style>

        <div style={{
          maxWidth: '1400px',
          margin: '0 auto',
          background: 'white',
          borderRadius: '12px',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
          padding: '12px',
          position: 'relative',
          color: 'black',
          fontFamily: 'Times New Romans',
        }}>
          {/* Header Section */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '10px',
            paddingBottom: '8px',
            borderBottom: '1px solid #f0f0f0'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <div>
                <h4 style={{
                  fontSize: '17px',
                  fontWeight: '700',
                  margin: 0,
                  letterSpacing: '-0.3px',
                  fontFamily: 'Times New Romans',
                  textTransform: 'uppercase'
                }}>
                  <span style={{ color: 'black', fontFamily: 'Times New Romans', }}>Delivery In</span>
                </h4>
              </div>
            </div>
          </div>

          {/* Date Filters, Search - Horizontally Scrollable */}
          <div style={{
            marginBottom: '12px',
            overflowX: 'auto',
            overflowY: 'hidden',
            WebkitOverflowScrolling: 'touch',
            scrollbarWidth: 'thin'
          }}>
            <div style={{
              display: 'flex',
              gap: '8px',
              alignItems: 'flex-end',
              minWidth: 'fit-content',
              paddingBottom: '4px'
            }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', minWidth: isMobile ? '120px' : '140px' }}>
                <label style={{ fontSize: '10px', color: '#666', paddingLeft: '4px' }}>From Date</label>
                <TextField
                  type="date"
                  size="small"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                  sx={{
                    width: isMobile ? '120px' : '140px',
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '6px',
                      backgroundColor: '#f8f9fa',
                      fontSize: '11px',
                      height: '32px',
                      '&:hover fieldset': { borderColor: '#667eea' },
                      '&.Mui-focused fieldset': { borderColor: '#667eea' },
                    },
                  }}
                />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', minWidth: isMobile ? '120px' : '140px' }}>
                <label style={{ fontSize: '10px', color: '#666', paddingLeft: '4px' }}>To Date</label>
                <TextField
                  type="date"
                  size="small"
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                  sx={{
                    width: isMobile ? '120px' : '140px',
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '6px',
                      backgroundColor: '#f8f9fa',
                      fontSize: '11px',
                      height: '32px',
                      '&:hover fieldset': { borderColor: '#667eea' },
                      '&.Mui-focused fieldset': { borderColor: '#667eea' },
                    },
                  }}
                />
              </div>

              {/* Search - Icon on mobile, always visible on desktop */}
              {isMobile ? (
                searchExpanded ? (
                  <TextField
                    size="small"
                    placeholder="Search..."
                    value={searchTerm}
                    onChange={handleSearchChange}
                    autoFocus
                    onBlur={() => {
                      if (!searchTerm) {
                        setSearchExpanded(false);
                      }
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon style={{ color: '#667eea', fontSize: '18px' }} />
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      minWidth: '250px',
                      width: '350px',
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '6px',
                        backgroundColor: '#f8f9fa',
                        fontSize: '12px',
                        height: '32px',
                        '&:hover fieldset': {
                          borderColor: '#667eea',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#667eea',
                        },
                      },
                    }}
                  />
                ) : (
                  <Tooltip title="Search" arrow>
                    <Button
                      onClick={() => setSearchExpanded(true)}
                      style={{
                        minWidth: '32px',
                        width: '32px',
                        height: '32px',
                        padding: '0',
                        borderRadius: '6px',
                        backgroundColor: '#f8f9fa',
                        border: '1px solid #e2e8f0',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      <SearchIcon style={{ color: '#667eea', fontSize: '18px' }} />
                    </Button>
                  </Tooltip>
                )
              ) : (
                <TextField
                  size="small"
                  placeholder="Search..."
                  value={searchTerm}
                  onChange={handleSearchChange}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon style={{ color: '#667eea', fontSize: '18px' }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    flex: 1,
                    minWidth: '250px',
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '6px',
                      backgroundColor: '#f8f9fa',
                      fontSize: '12px',
                      height: '32px',
                      '&:hover fieldset': {
                        borderColor: '#667eea',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#667eea',
                      },
                    },
                  }}
                />
              )}
            </div>
          </div>

          {/* Unique Loading Overlay */}
          {loading && (
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '48px 0',
              gap: '16px'
            }}>
              <style>{`
                @keyframes deliverySpin {
                  0% { transform: rotate(0deg); }
                  100% { transform: rotate(360deg); }
                }
                @keyframes deliveryPulse {
                  0%, 100% { opacity: 1; transform: scale(1); }
                  50% { opacity: 0.5; transform: scale(0.92); }
                }
                @keyframes deliveryDot {
                  0%, 80%, 100% { transform: scale(0); opacity: 0.3; }
                  40% { transform: scale(1); opacity: 1; }
                }
              `}</style>

              <div style={{ position: 'relative', width: '72px', height: '72px' }}>
                <div style={{
                  position: 'absolute', inset: 0,
                  borderRadius: '50%',
                  border: '3px solid #e0e7ff',
                  borderTopColor: '#667eea',
                  animation: 'deliverySpin 0.9s linear infinite'
                }} />
                <div style={{
                  position: 'absolute', inset: '10px',
                  borderRadius: '50%',
                  border: '3px solid #ede9fe',
                  borderBottomColor: '#764ba2',
                  animation: 'deliverySpin 1.3s linear infinite reverse'
                }} />
                <div style={{
                  position: 'absolute', inset: '20px',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: '18px',
                  animation: 'deliveryPulse 1.5s ease-in-out infinite'
                }}>
                  🚚
                </div>
              </div>

              <div style={{ display: 'flex', gap: '6px' }}>
                {[0, 1, 2].map(i => (
                  <div key={i} style={{
                    width: '8px', height: '8px',
                    borderRadius: '50%',
                    backgroundColor: '#667eea',
                    animation: `deliveryDot 1.2s ease-in-out ${i * 0.2}s infinite`
                  }} />
                ))}
              </div>

              <p style={{ margin: 0, fontSize: '12px', color: '#4c1d95', fontWeight: 600, letterSpacing: '0.5px' }}>
                Loading Deliveries...
              </p>
            </div>
          )}

          {/* Table */}
          <div style={{ marginBottom: '10px', display: loading ? 'none' : 'block' }}>
            <TableContainer component={Paper} style={{
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
              borderRadius: '10px',
              overflow: 'hidden'
            }}>
              <Table sx={{ minWidth: 650 }} aria-label="delivery in table">
                <TableHead>
                  <TableRow style={{
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    height: '28px'
                  }}>
                    {columns.map((column) => (
                      <TableCell key={column.field} style={{
                        color: 'white',
                        fontWeight: '600',
                        fontSize: '10px',
                        textTransform: 'uppercase',
                        letterSpacing: '0.3px',
                        padding: '4px 8px',
                        textAlign: 'left',
                        height: '28px'
                      }}>
                        {column.header}
                      </TableCell>
                    ))}
                    <TableCell style={{
                      color: 'white',
                      fontWeight: '600',
                      fontSize: '10px',
                      textTransform: 'uppercase',
                      letterSpacing: '0.3px',
                      padding: '4px 8px',
                      textAlign: 'right',
                      height: '28px'
                    }}>
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedData.map((item, index) => (
                    <TableRow key={item.Id} className="table-row" style={{
                      background: index % 2 === 0 ? '#ffffff' : '#f8f9fa',
                      borderBottom: '2px solid #f0f0f0'
                    }}>
                      <TableCell style={{ fontSize: '12px', padding: '8px 12px', color: '#2d3748' }}>
                        {item.DeliveryNo || 'N/A'}
                      </TableCell>
                      <TableCell style={{ fontSize: '12px', padding: '8px 12px', color: '#2d3748' }}>
                        {item.CustomerName || 'N/A'}
                      </TableCell>
                      <TableCell style={{ fontSize: '12px', padding: '8px 12px', color: '#2d3748' }}>
                        {item.ContactNumber || 'N/A'}
                      </TableCell>
                      <TableCell style={{ fontSize: '12px', padding: '8px 12px', color: '#2d3748' }}>
                        {item.AddressLine || 'N/A'}
                      </TableCell>
                      <TableCell style={{ fontSize: '12px', padding: '8px 12px', color: '#2d3748' }}>
                        {formatDate(item.DeliveryReturnDate)}
                      </TableCell>
                      <TableCell style={{ fontSize: '12px', padding: '8px 12px' }}>
                        {item.IsInvoiced
                          ? <span style={{ background: '#d1fae5', color: '#065f46', borderRadius: '4px', padding: '2px 8px', fontSize: '11px', fontWeight: 600 }}>Raised Invoice</span>
                          : <span style={{ background: '#f3f4f6', color: '#6b7280', borderRadius: '4px', padding: '2px 8px', fontSize: '11px', fontWeight: 600 }}>Pending</span>}
                      </TableCell>
                      <TableCell style={{ padding: '8px 12px', textAlign: 'right' }}>
                        {renderActions(item)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </div>

          {/* Pagination */}
          {!loading && <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            totalItems={totalItems}
            itemsPerPage={itemsPerPage}
            onPageChange={handlePageChange}
            onItemsPerPageChange={handleItemsPerPageChange}
            pageSizeOptions={[10, 25, 50, 100]}
          />}
        </div>

        {/* Delete Confirmation Dialog */}
        <Dialog
          open={confirmDialogOpen}
          onClose={closeConfirmDialog}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
          PaperProps={{
            style: {
              borderRadius: '12px',
              padding: '8px'
            }
          }}
        >
          <DialogTitle id="alert-dialog-title" style={{
            fontSize: '16px',
            fontWeight: '600',
            color: '#2d3748'
          }}>
            Delete Record?
          </DialogTitle>
          <DialogContent>
            <DialogContentText id="alert-dialog-description" style={{
              fontSize: '13px',
              color: '#666'
            }}>
              Are you sure you want to delete this delivery in record?
            </DialogContentText>
          </DialogContent>
          <DialogActions style={{ padding: '8px 12px', gap: '6px' }}>
            <Button
              onClick={closeConfirmDialog}
              style={{
                background: 'linear-gradient(135deg, #ff6b6b 0%, #ee5a6f 100%)',
                color: '#ffffff',
                borderRadius: '6px',
                padding: '4px 8px',
                fontSize: '12px',
                fontWeight: '600',
                textTransform: 'none',
                boxShadow: '0 1px 4px rgba(255, 107, 107, 0.3)',
                border: 'none',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                minHeight: '26px'
              }}
            >
              <span style={{ fontSize: '12px' }}>❌</span>
              Cancel
            </Button>
            <Button
              onClick={handleDelete}
              style={{
                background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
                color: '#ffffff',
                borderRadius: '6px',
                padding: '4px 8px',
                fontSize: '12px',
                fontWeight: '600',
                textTransform: 'none',
                boxShadow: '0 1px 4px rgba(17, 153, 142, 0.3)',
                border: 'none',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                minHeight: '26px'
              }}
            >
              <span style={{ fontSize: '12px' }}>✅</span>
              Confirm Delete
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delivery In Modal */}
        <DeliveryInModal
          open={modalOpen}
          onClose={handleCloseModal}
          onSave={handleSaveDeliveryIn}
          deliveryData={selectedItem}
          mode={modalMode}
        />

        {/* Rent Invoice Modal */}
        <RentInvoiceModal
          open={invoiceModalOpen}
          onClose={handleCloseInvoiceModal}
          deliveryData={selectedInvoiceItem}
          mode="create"
          onSave={fetchTableData}
        />
       
      </div>
    </Fade>
  );
}

export default DeliveryInIndex;
